import React, { useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, Paper } from '@mui/material';

// Fix for default marker icons in React-Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Country coordinates (centroid of each country)
const countryCoordinates: { [key: string]: [number, number] } = {
  'United States': [39.8283, -98.5795],
  'United Kingdom': [55.3781, -3.4360],
  'Canada': [56.1304, -106.3468],
  'Australia': [-25.2744, 133.7751],
  'India': [20.5937, 78.9629],
  'Germany': [51.1657, 10.4515],
  'France': [46.2276, 2.2137],
  'Italy': [41.8719, 12.5674],
  'Spain': [40.4637, -3.7492],
  'Brazil': [-14.2350, -51.9253],
  'Mexico': [23.6345, -102.5528],
  'Japan': [36.2048, 138.2529],
  'China': [35.8617, 104.1954],
  'South Korea': [35.9078, 127.7669],
  'Russia': [61.5240, 105.3188],
  'Turkey': [38.9637, 35.2433],
  'Saudi Arabia': [23.8859, 45.0792],
  'United Arab Emirates': [23.4241, 53.8478],
  'Egypt': [26.0975, 30.0444],
  'South Africa': [-30.5595, 22.9375],
  'Nigeria': [9.0820, 8.6753],
  'Kenya': [-0.0236, 37.9062],
  'Pakistan': [30.3753, 69.3451],
  'Bangladesh': [23.6850, 90.3563],
  'Indonesia': [-0.7893, 113.9213],
  'Philippines': [12.8797, 121.7740],
  'Vietnam': [14.0583, 108.2772],
  'Thailand': [15.8700, 100.9925],
  'Malaysia': [4.2105, 101.9758],
  'Singapore': [1.3521, 103.8198],
  'New Zealand': [-40.9006, 174.8860],
  'Netherlands': [52.1326, 5.2913],
  'Belgium': [50.5039, 4.4699],
  'Switzerland': [46.8182, 8.2275],
  'Austria': [47.5162, 14.5501],
  'Sweden': [60.1282, 18.6435],
  'Norway': [60.4720, 8.4689],
  'Denmark': [56.2639, 9.5018],
  'Finland': [61.9241, 25.7482],
  'Poland': [51.9194, 19.1451],
  'Portugal': [39.3999, -8.2245],
  'Greece': [39.0742, 21.8243],
  'Ireland': [53.4129, -8.2439],
  'Argentina': [-38.4161, -63.6167],
  'Chile': [-35.6751, -71.5430],
  'Colombia': [4.5709, -74.2973],
  'Peru': [-9.1900, -75.0152],
  'Venezuela': [6.4238, -66.5897],
};

interface CountryData {
  country: string;
  count: number;
}

interface CountryHeatmapProps {
  countryCounts: CountryData[];
}

// Component to fit map bounds to markers
function FitBounds({ countryCounts }: { countryCounts: CountryData[] }) {
  const map = useMap();
  
  useEffect(() => {
    if (countryCounts.length === 0) return;
    
    const bounds = countryCounts
      .filter(item => countryCoordinates[item.country])
      .map(item => countryCoordinates[item.country]);
    
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [countryCounts, map]);
  
  return null;
}

const CountryHeatmap: React.FC<CountryHeatmapProps> = ({ countryCounts }) => {
  const maxCount = Math.max(...countryCounts.map(item => item.count), 1);
  
  // Get color intensity based on count
  const getColor = (count: number) => {
    const intensity = count / maxCount;
    if (intensity > 0.7) return '#d32f2f'; // Red - high
    if (intensity > 0.4) return '#f57c00'; // Orange - medium-high
    if (intensity > 0.2) return '#fbc02d'; // Yellow - medium
    return '#66bb6a'; // Green - low
  };
  
  // Get radius based on count
  const getRadius = (count: number) => {
    const intensity = count / maxCount;
    return 5 + (intensity * 20); // Radius between 5 and 25
  };

  if (countryCounts.length === 0) {
    return (
      <Paper sx={{ p: 2, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No country data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 500, borderRadius: 1, overflow: 'hidden' }}>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds countryCounts={countryCounts} />
        {countryCounts.map((item) => {
          const coords = countryCoordinates[item.country];
          if (!coords) return null;
          
          return (
            <CircleMarker
              key={item.country}
              center={coords}
              radius={getRadius(item.count)}
              pathOptions={{
                fillColor: getColor(item.count),
                color: '#fff',
                fillOpacity: 0.7,
                weight: 2,
              }}
            >
              <Popup>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.country}
                </Typography>
                <Typography variant="body2">
                  Images: {item.count}
                </Typography>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </Box>
  );
};

export default CountryHeatmap;

